import {
  addDays,
  clamp,
  differenceInDays,
  differenceInYears,
  format,
  isValid,
  max as maxDate,
  parseISO,
} from "date-fns";
import { i18n } from "next-i18next";
import {
  BreedType,
  ProductEnum,
  SpeciesEnum,
} from "../../__generated__/graphql";
import { NullableString } from "../types";
import {
  getMaxStartOffsetInDays,
  getMinimumAgeInDays,
  getStartOffsetInDays,
  isOrg,
} from "./site";
import { MAX_AGE_CAT, MAX_AGE_DOG } from "components-shared/insuranceData";
import { formatCurrency } from "./currency";
import { dateValid } from "./validation";
import { Breed } from "components/Signup/store/state/types";
import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";

export const MONTHLY_BILLING_INTERVAL = 1;
export const YEARLY_BILLING_INTERVAL = 12;

export type BillingInterval =
  | typeof YEARLY_BILLING_INTERVAL
  | typeof MONTHLY_BILLING_INTERVAL;

export const planName = (
  limit: number | undefined,
  species: SpeciesEnum | null
): string => {
  //TODO: Handle limit for different orgs better
  if (species === SpeciesEnum.dog) {
    if (limit === 0) {
      return i18n?.t("plan_name.op") || "";
    }
    if (limit === 3000000 || limit === 300000) {
      return i18n?.t("plan_name.mini") || "";
    }
    if (limit === 6000000 || limit === 100000) {
      return i18n?.t("plan_name.between") || "";
    }
    if (limit === 16000000 || limit === 9999999900) {
      return i18n?.t("plan_name.big") || "";
    }
  }
  if (species === SpeciesEnum.cat) {
    if (limit === 0) {
      return i18n?.t("plan_name.op") || "";
    }
    if (limit === 3000000 || limit === 300000) {
      return i18n?.t("plan_name.mini") || "";
    }
    if (limit === 6000000 || limit === 100000) {
      return i18n?.t("plan_name.between") || "";
    }
    if (limit === 16000000 || limit === 9999999900) {
      return i18n?.t("plan_name.big") || "";
    }
  }
  return i18n?.t("plan_name.liability") || "";
};

export const isUnlimitedLimit = (limit: number) =>
  limit === 0 || limit === 9999999900;

export const breedToSpecies = (
  breed: NullableString
): SpeciesEnum | undefined => {
  if (breed?.includes("DogBreed")) {
    return SpeciesEnum.dog;
  }
  if (breed?.includes("CatBreed")) {
    return SpeciesEnum.cat;
  }
  return undefined;
};

export const resparkProductToSpecies = (product: ProductEnum | null) => {
  return product === ProductEnum.liability || product === ProductEnum.dog
    ? SpeciesEnum.dog
    : SpeciesEnum.cat;
};

export const productEnumToSignupProductEnum = (
  product: ProductEnum
): SignUpProductEnum => {
  if (product === ProductEnum.liability) {
    return SignUpProductEnum.liability;
  }

  return SignUpProductEnum.health;
};

export const speciesToEmoji = (
  species: SpeciesEnum | undefined | null
): string => {
  if (species === SpeciesEnum.cat) {
    return "🐱";
  }
  if (species === SpeciesEnum.dog) {
    return "🐶";
  }
  return "";
};

export const speciesName = (
  species: SpeciesEnum | undefined | null
): string => {
  if (species === SpeciesEnum.cat) {
    return i18n?.t("global.cat") || "";
  }
  if (species === SpeciesEnum.dog) {
    return i18n?.t("global.dog") || "";
  }
  return "";
};

export const himHer = (sex: string): string => {
  if (sex === "male") {
    return i18n?.t("sex.male") || "";
  }
  if (sex === "female") {
    return i18n?.t("sex.female") || "";
  }
  return "";
};

export const animalPronoun = (
  sex: "male" | "female",
  species: SpeciesEnum | undefined | null
): string => {
  if (species === SpeciesEnum.dog) {
    if (sex === "female") {
      return i18n?.t("sex.dog_pronoun_female") || "";
    } else {
      return i18n?.t("sex.dog_pronoun_male") || "";
    }
  } else if (species === SpeciesEnum.cat) {
    if (sex === "female") {
      return i18n?.t("sex.cat_pronoun_female") || "";
    } else {
      return i18n?.t("sex.cat_pronoun_male") || "";
    }
  }
  return "";
};

export const amountToPay = ({
  total = 0,
  netPremium = 0,
  discounts = [],
  billingInterval,
}: {
  total?: number;
  netPremium?: number;
  discounts?: { maxInvoicesSpread?: number | null }[] | null;
  billingInterval?: number | null;
}) => {
  //Don't show with discount if we have first x months free
  return !!discounts?.[0]?.maxInvoicesSpread
    ? (netPremium / 100 / 12) * (billingInterval || 1)
    : (total / 100 / 12) * (billingInterval || 1);
};

const ageInDays = (date: string): number => {
  return differenceInDays(new Date(), new Date(date));
};

// Plausibility floor for pet birthdays in markets with no max-age cap.
export const EARLIEST_PET_BIRTHDAY = new Date(1900, 0, 1);

/**
 * Max signup age in months for the current market, or null when uncapped.
 * GBR insures pets with no upper age limit (the UK pricing model extrapolates
 * past its age table); the other markets keep the fixed cap.
 */
export const maxSignupAgeMonths = (
  species: SpeciesEnum | null
): number | null =>
  isOrg("gbr") ? null : species === SpeciesEnum.dog ? MAX_AGE_DOG : MAX_AGE_CAT;

export const getEarliestAllowedStartDate = () => {
  return addDays(new Date(), getStartOffsetInDays());
};

/**
 * Window the market allows from today (default 364; one day too small on leap
 * years — fixing needs the backend fixed first). Markets can shorten it via
 * insurance.maxStartOffsetInDays (GBR = 60).
 */
export const getLastAllowedStartDate = () =>
  addDays(new Date(), getMaxStartOffsetInDays());

/**
 * Last selectable start date for an animal: the market window, but never before
 * the earliest insurable date. With a short window (GBR's 60 days) an age-gated
 * earliest can land after today+window; without this the range would be empty
 * and the native picker would lock with no selectable date.
 */
export const getLastAllowedStartDateForAnimal = (birthday: string): Date =>
  maxDate([getLastAllowedStartDate(), getEarliestStartDate(birthday)]);

/**
 * The range a quote page's start-date field accepts, in every market: from the
 * market's start offset — later if the pet is not yet old enough — to the end
 * of the market's window. Never the date already chosen: used as the minimum,
 * that stopped a customer from ever moving the start earlier.
 */
export const startDateBounds = (birthday: string) => ({
  min: getEarliestStartDate(birthday),
  max: getLastAllowedStartDateForAnimal(birthday),
});

export const validStartDate = ({
  birthday,
  startDate,
}: {
  birthday: string;
  startDate?: string | null;
}): string => {
  const possibleStartDate =
    startDate && isValid(parseISO(startDate))
      ? new Date(startDate)
      : getEarliestAllowedStartDate();

  const earliest = birthday
    ? getEarliestStartDate(birthday)
    : getEarliestAllowedStartDate();

  return format(
    clamp(possibleStartDate, {
      start: earliest,
      end: maxDate([getLastAllowedStartDate(), earliest]),
    }),
    "yyyy-MM-dd"
  );
};

export const amountStatement = (
  amount: number,
  billingInterval: number
): string => `${formatCurrency(amount)} ${amountSuffix(billingInterval)}`;

export const amountSuffix = (billingInterval: number) =>
  `/ ${
    billingInterval === 12
      ? i18n?.t("global.date.year")
      : i18n?.t("global.date.month")
  }`;

export const resparkBreedsString = (breed: Breed | null): string =>
  breed?.breedIds.join(",") ?? "";

export const resparkSexString = (sex: string, sexSituation: string): string =>
  `${sex}-${sexSituation}`;

export const getEarliestStartDate = (birthday: string): Date => {
  const MIN_AGE_IN_DAYS = getMinimumAgeInDays();

  return ageInDays(birthday) >= MIN_AGE_IN_DAYS
    ? getEarliestAllowedStartDate()
    : addDays(new Date(), MIN_AGE_IN_DAYS - ageInDays(birthday));
};

export const userAbove18 = (userBirthdate: string) => {
  return (
    dateValid(userBirthdate) &&
    differenceInYears(new Date(), new Date(userBirthdate)) >= 18
  );
};

// GBR limits the owner's age to 18–99 years.
export const userBetween18And99 = (userBirthdate: string) => {
  if (!dateValid(userBirthdate)) {
    return false;
  }

  const age = differenceInYears(new Date(), new Date(userBirthdate));

  return age >= 18 && age <= 99;
};

export const getDiscountDisplayValue = ({
  discount,
  billingInterval,
}: {
  discount: {
    maxInvoicesSpread: number;
    total: number;
    name: string;
  };
  billingInterval: number;
}) => {
  return (
    -1 *
    (((discount.total /
      (billingInterval === YEARLY_BILLING_INTERVAL
        ? 12
        : discount.maxInvoicesSpread ?? 12)) *
      billingInterval) /
      100)
  );
};
